import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { USERS_API_URL } from '../../config';
import { readFromStorage } from '../../utils/webStorage';

export type AgentType = 'claire' | 'leo' | 'rebecca';
export type AgentClientType = 'sofia' | 'leo' | 'charles' | 'claire' | 'rebecca' | 'filipa' | 'iris' | 'martin';

export const AGENT_CLIENTS: AgentClientType[] = [
    'sofia',
    'leo',
    'charles',
    'claire',
    'rebecca',
    'filipa',
    'iris',
    'martin',
];

export interface IUserRow {
    id: string;
    name: string;
    email: string;
    username: string;
    agent: AgentType | null;
    clientId: string;
    /** Present when API includes a non-empty `widgetConfigs` array */
    hasWidgetConfigs: boolean;
    widgetPermissions?: Partial<
        Record<
            AgentType,
            {
                personalWidgetEnabled?: boolean;
                agentWidgetEnabled?: boolean;
            }
        >
    >;
}

export interface IUserWidgetOptions {
    showWidgetForClients: boolean;
    showWidgetForUsers: boolean;
}

export interface IAgentClientsFilters {
    search?: string;
    agent?: AgentClientType | '';
    enabled?: boolean;
}

export interface IAgentClientsRequest {
    pageNumber?: number;
    pageSize?: number;
    data?: IAgentClientsFilters;
}

export interface ICreateAgentClientRequest {
    agents: AgentClientType[];
    name: string;
    email: string;
    username: string;
    password: string;
    country: string;
    companyName?: string;
}

export interface IResetAgentClientPasswordRequest {
    username: string;
    password: string;
}

export interface IAgentClientRow {
    id: string;
    companyName: string;
    name: string;
    email: string;
    username: string;
    agents: AgentClientType[];
    enabled?: boolean;
    clientId?: string;
}

export interface IAgentClientsResponse {
    elements: IAgentClientRow[];
    page?: {
        number?: number;
        pageNumber?: number;
        totalPages?: number;
        totalElements?: number;
        hasNextPage?: boolean;
    };
}

export interface ITipsFeedbackClientOption {
    id: string;
    label: string;
    name?: string;
    companyName?: string;
}

export interface ITipsFeedbackPropertyOption {
    companyName: string;
    label: string;
    clientId?: string;
}

export interface IComparisonBoardClientOption {
    id: string;
    label: string;
    username: string;
    companySub: string;
    name?: string;
    companyName?: string;
    enabled?: boolean;
}

export interface IComparisonBoardPropertyOption {
    id: string;
    label: string;
    propertyName: string;
    companySub: string;
}

interface IUsersApiWidgetConfig {
    agent?: string;
}

interface IUsersApiElement {
    _id: string;
    name?: string;
    email?: string;
    username?: string;
    agents?: string[];
    sub?: string;
    companyData?: {
        sub?: string;
    };
    widgetConfigs?: IUsersApiWidgetConfig[];
    widgetPermissions?: Partial<
        Record<
            string,
            {
                personalWidgetEnabled?: boolean;
                agentWidgetEnabled?: boolean;
            }
        >
    >;
}

interface IUsersApiResponse {
    success: boolean;
    status: number;
    aggregationId: string;
    data?: {
        elements?: IUsersApiElement[];
    };
}

interface IAgentClientApiElement extends IUsersApiElement {
    id?: string;
    enabled?: boolean;
    agent?: string;
    companyName?: string;
    company?: {
        name?: string;
    };
    companyData?: {
        sub?: string;
        username?: string;
        companyName?: string;
        name?: string;
    };
    client?: {
        id?: string;
        _id?: string;
        sub?: string;
        name?: string;
        companyName?: string;
        email?: string;
        username?: string;
    };
}

const resolveElements = (response: any): any[] => {
    const payload = response?.data ?? response ?? {};
    const elements = payload?.elements ?? payload?.data?.elements ?? payload?.data ?? payload;
    if (Array.isArray(elements)) return elements;
    return [];
};

const normalizeText = (value: unknown) => String(value || '').trim();

const toUniqueBy = <T>(list: T[], keyFn: (item: T) => string) => {
    const map = new Map<string, T>();
    list.forEach((item) => {
        const key = keyFn(item);
        if (key && !map.has(key)) {
            map.set(key, item);
        }
    });
    return Array.from(map.values());
};

const toTipsFeedbackClientOption = (item: any): ITipsFeedbackClientOption | null => {
    const id = normalizeText(
        item?._id
        || item?.id
        || item?.clientId
        || item?.sub
        || item?.companyData?.sub,
    );
    if (!id) return null;

    const name = normalizeText(
        item?.name
        || item?.username
        || item?.email
        || item?.client?.name,
    );
    const companyName = normalizeText(
        item?.companyName
        || item?.company?.name
        || item?.hotelName,
    );

    const label = name && companyName && name.toLowerCase() !== companyName.toLowerCase()
        ? `${name} - ${companyName}`
        : companyName || name || id;

    return {
        id,
        label,
        name: name || undefined,
        companyName: companyName || undefined,
    };
};

const toTipsFeedbackPropertyOption = (item: any): ITipsFeedbackPropertyOption | null => {
    const companyName = normalizeText(
        item?.companyName
        || item?.property
        || item?.name,
    );
    if (!companyName) return null;

    const clientId = normalizeText(
        item?.clientId
        || item?.client?._id
        || item?.client?.id
        || item?.client?.sub
        || item?.superAdminId
        || item?.ownerId
        || item?.userId,
    ) || undefined;

    return {
        companyName,
        label: companyName,
        clientId,
    };
};

const toComparisonBoardClientOption = (item: any): IComparisonBoardClientOption | null => {
    const companySub = normalizeText(
        item?.companyData?.sub
        || item?.companySub
        || item?.client?.companyData?.sub
        || item?.client?.companySub
        || item?.client?.sub
        || item?.sub,
    );
    const username = normalizeText(
        item?.username
        || item?.companyData?.username
        || item?.client?.username
        || item?.client?.companyData?.username,
    );
    const id = normalizeText(item?._id || item?.id || username || companySub);
    if (!id || !username || !companySub) return null;

    const name = normalizeText(item?.name || item?.client?.name);
    const companyName = normalizeText(
        item?.companyName
        || item?.company?.name
        || item?.companyData?.companyName
        || item?.companyData?.name
        || item?.client?.companyName
        || item?.client?.companyData?.companyName,
    );

    return {
        id,
        label: companyName && companyName.toLowerCase() !== username.toLowerCase()
            ? `${username} - ${companyName}`
            : username,
        username,
        companySub,
        name: name || undefined,
        companyName: companyName || undefined,
        enabled: typeof item?.enabled === 'boolean' ? item.enabled : undefined,
    };
};

const toComparisonBoardPropertyOption = (item: any): IComparisonBoardPropertyOption | null => {
    const propertyName = normalizeText(
        item?.propertyName
        || item?.companyName
        || item?.property
        || item?.name,
    );
    const companySub = normalizeText(
        item?.companySub
        || item?.companyData?.sub
        || item?.company?.sub
        || item?.client?.companySub
        || item?.client?.sub,
    );
    if (!propertyName || !companySub) return null;

    const id = normalizeText(item?._id || item?.id || `${companySub}-${propertyName}`);

    return {
        id,
        label: propertyName,
        propertyName,
        companySub,
    };
};

const isAgentType = (value: unknown): value is AgentType =>
    value === 'claire' || value === 'leo' || value === 'rebecca';

const normalizeAgentClientType = (value: unknown): AgentClientType | null => {
    if (typeof value !== 'string') return null;

    const normalizedValue = value.trim().toLowerCase() as AgentClientType;
    return AGENT_CLIENTS.includes(normalizedValue) ? normalizedValue : null;
};

const extractAgent = (element: IUsersApiElement): AgentType | null => {
    const widgetConfigAgent = element.widgetConfigs?.find((cfg) => isAgentType(cfg?.agent))?.agent;
    if (widgetConfigAgent && isAgentType(widgetConfigAgent)) return widgetConfigAgent;

    const directAgent = element.agents?.find((agent) => isAgentType(agent));
    if (directAgent && isAgentType(directAgent)) return directAgent;

    const permissionAgent = Object.keys(element.widgetPermissions || {}).find((agent) =>
        isAgentType(agent),
    );
    if (permissionAgent && isAgentType(permissionAgent)) return permissionAgent;

    return null;
};

const extractAgentClients = (element: IAgentClientApiElement): AgentClientType[] => {
    const normalizeAgents = (candidates: unknown[]) => Array.from(new Set(
        candidates
            .map(normalizeAgentClientType)
            .filter((agent): agent is AgentClientType => agent !== null),
    ));

    const directAgents = normalizeAgents(
        Array.isArray(element.agents) ? element.agents : [],
    );
    if (directAgents.length > 0) return directAgents;

    const legacyAgent = normalizeAgentClientType(element.agent);
    if (legacyAgent) return [legacyAgent];

    const widgetConfigAgents = normalizeAgents(
        (element.widgetConfigs || []).map((config) => config.agent),
    );
    if (widgetConfigAgents.length > 0) return widgetConfigAgents;

    return normalizeAgents(Object.keys(element.widgetPermissions || {}));
};

const buildAgentClientsUrl = (pageNumber?: number, pageSize?: number) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNumber || 1));
    params.set('size', String(pageSize || 10));
    return `${USERS_API_URL}/rebecca-users/users/clients/all?${params.toString()}`;
};

const toAgentClientRow = (client: IAgentClientApiElement): IAgentClientRow => {
    const id = normalizeText(client?._id || client?.id || client?.sub || client?.client?.id || client?.client?._id);
    const companyName = normalizeText(
        client?.companyName
        || client?.company?.name
        || client?.companyData?.companyName
        || client?.companyData?.name
        || client?.client?.companyName,
    );
    const name = normalizeText(client?.name || client?.client?.name);
    const email = normalizeText(client?.email || client?.client?.email);
    const username = normalizeText(
        client?.username
        || client?.companyData?.username
        || client?.client?.username,
    );
    const clientId = normalizeText(
        client?.companyData?.sub
        || client?.client?.sub
        || client?.client?.id
        || client?.client?._id
        || client?.sub,
    );

    return {
        id,
        companyName: companyName || '--',
        name: name || '--',
        email: email || '--',
        username: username || '--',
        agents: extractAgentClients(client),
        enabled: typeof client?.enabled === 'boolean' ? client.enabled : undefined,
        clientId: clientId || undefined,
    };
};

export const usersApi = createApi({
    reducerPath: 'usersApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: (headers) => {
            const token = readFromStorage('accessToken');
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Users', 'UserWidgetOptions'],
    endpoints: (builder) => ({
        getUsersList: builder.query<IUserRow[], { page?: number; limit?: number } | void>({
            query: () => ({
                url: `${USERS_API_URL}/rebecca-users/users/clients`,
                method: 'GET',
            }),
            transformResponse: (response: IUsersApiResponse) => {
                const elements = response?.data?.elements ?? [];

                return elements.map((user) => ({
                    id: user._id,
                    name: user.name ?? '--',
                    email: user.email ?? '--',
                    username: user.username ?? '--',
                    agent: extractAgent(user),
                    clientId: user.companyData?.sub ?? user.sub ?? '',
                    hasWidgetConfigs: Array.isArray(user.widgetConfigs) && user.widgetConfigs.length > 0,
                    widgetPermissions: user.widgetPermissions as IUserRow['widgetPermissions'],
                }));
            },
            providesTags: ['Users'],
        }),
        getTipsFeedbackClients: builder.query<ITipsFeedbackClientOption[], void>({
            query: () => ({
                url: `${USERS_API_URL}/rebecca-users/clients/super-admins`,
                method: 'GET',
            }),
            transformResponse: (response: any) =>
                toUniqueBy(
                    resolveElements(response)
                        .map(toTipsFeedbackClientOption)
                        .filter(Boolean) as ITipsFeedbackClientOption[],
                    (item) => item.id,
                ).sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        }),
        getTipsFeedbackProperties: builder.query<ITipsFeedbackPropertyOption[], void>({
            query: () => ({
                url: `${USERS_API_URL}/rebecca-users/properties/finished-setup`,
                method: 'GET',
            }),
            transformResponse: (response: any) =>
                toUniqueBy(
                    resolveElements(response)
                        .map(toTipsFeedbackPropertyOption)
                        .filter(Boolean) as ITipsFeedbackPropertyOption[],
                    (item) => `${item.clientId || ''}::${item.companyName.toLowerCase()}`,
                ).sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        }),
        getComparisonBoardClients: builder.query<IComparisonBoardClientOption[], void>({
            query: () => ({
                url: `${USERS_API_URL}/rebecca-users/clients/super-admins`,
                method: 'GET',
            }),
            transformResponse: (response: any) =>
                toUniqueBy(
                    resolveElements(response)
                        .map(toComparisonBoardClientOption)
                        .filter(Boolean) as IComparisonBoardClientOption[],
                    (item) => item.username,
                ).sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        }),
        getComparisonBoardProperties: builder.query<IComparisonBoardPropertyOption[], void>({
            query: () => ({
                url: `${USERS_API_URL}/rebecca-users/properties/finished-setup`,
                method: 'GET',
            }),
            transformResponse: (response: any) =>
                toUniqueBy(
                    resolveElements(response)
                        .map(toComparisonBoardPropertyOption)
                        .filter(Boolean) as IComparisonBoardPropertyOption[],
                    (item) => `${item.companySub}::${item.propertyName.toLowerCase()}`,
                ).sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        }),
        getUserWidgetOptions: builder.query<IUserWidgetOptions, string>({
            query: (userId) => `/posts/${userId}`,
            transformResponse: (response: { id: number }) => ({
                // Placeholder mapping from 3rd-party API response.
                showWidgetForClients: response?.id % 2 === 0,
                showWidgetForUsers: response?.id % 3 === 0,
            }),
            providesTags: (_, __, userId) => [{ type: 'UserWidgetOptions', id: userId }],
        }),
        updateUserWidgetOptions: builder.mutation<
            { success?: boolean },
            {
                agent: AgentType;
                clientId: string;
                personalWidgetEnabled?: boolean;
                agentWidgetEnabled?: boolean;
            }
        >({
            query: (data) => ({
                url: `${USERS_API_URL}/rebecca-users/users/widgets-control`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Users'],
        }),
        getAgentClients: builder.mutation<IAgentClientsResponse, IAgentClientsRequest>({
            query: (data) => ({
                url: buildAgentClientsUrl(data?.pageNumber, data?.pageSize),
                method: 'PUT',
                body: data?.data || {},
            }),
            transformResponse: (response: any): IAgentClientsResponse => {
                const normalized = response?.data?.data || response?.data || response || {};
                const elements = normalized?.elements || [];

                return {
                    elements: Array.isArray(elements)
                        ? elements.map((client: IAgentClientApiElement) => toAgentClientRow(client))
                        : [],
                    page: normalized?.page,
                };
            },
        }),
        createAgentClient: builder.mutation<{ success?: boolean; message?: string }, ICreateAgentClientRequest>({
            query: (data) => ({
                url: `${USERS_API_URL}/rebecca-users/clients/create`,
                method: 'POST',
                body: data,
            }),
        }),
        resetAgentClientPassword: builder.mutation<
            { success?: boolean; message?: string },
            IResetAgentClientPasswordRequest
        >({
            query: (data) => ({
                url: `${USERS_API_URL}/rebecca-users/clients/reset-password`,
                method: 'PUT',
                body: data,
            }),
        }),
    }),
});

export const {
    useCreateAgentClientMutation,
    useGetAgentClientsMutation,
    useResetAgentClientPasswordMutation,
    useGetUsersListQuery,
    useGetComparisonBoardClientsQuery,
    useGetComparisonBoardPropertiesQuery,
    useGetTipsFeedbackClientsQuery,
    useGetTipsFeedbackPropertiesQuery,
    useGetUserWidgetOptionsQuery,
    useUpdateUserWidgetOptionsMutation,
} = usersApi;
